import type { EmblaCarouselType } from 'embla-carousel'
import cn from 'classnames'
import React, {
  type ComponentPropsWithRef,
  useCallback,
  useEffect,
  useState,
} from 'react'
import styles from './CarouselDotButton.module.css'

interface UseDotButtonType {
  selectedIndex: number
  scrollSnaps: number[]
  onDotButtonClick: (index: number) => void
}
export function useDotButton(emblaApi: EmblaCarouselType | undefined): UseDotButtonType {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])

  const onDotButtonClick = useCallback(
    (index: number) => {
      if (!emblaApi)
        return
      emblaApi.scrollTo(index)
    },
    [emblaApi],
  )

  const onInit = useCallback((emblaApi: EmblaCarouselType) => {
    setScrollSnaps(emblaApi.scrollSnapList())
  }, [])

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [])

  useEffect(() => {
    if (!emblaApi)
      return

    onInit(emblaApi)
    onSelect(emblaApi)
    emblaApi.on('reInit', onInit)
    emblaApi.on('select', onSelect)
    return () => {
      emblaApi.off('reInit', onInit)
      emblaApi.off('select', onSelect)
    }
  }, [emblaApi, onInit, onSelect])

  return {
    selectedIndex,
    scrollSnaps,
    onDotButtonClick,
  }
}

type PropType = ComponentPropsWithRef<'button'> & {
  selected?: boolean
}

export const DotButton: React.FC<PropType> = (props) => {
  const { children, selected = false, className, ...restProps } = props
  return (
    <button
      type="button"
      className={cn(styles.dot, selected && styles.dotSelected, className)}
      {...restProps}
    >
      {children}
    </button>
  )
}
